import { Subscription, map, mergeMap, from, of, catchError, EMPTY, tap, finalize, identity, filter, Subject, observeOn, queueScheduler, Observable } from 'rxjs';

const miniRxNameSpace = '@mini-rx';

function miniRxError(message) {
  throw new Error(miniRxNameSpace + ': ' + message);
}

function createSubSink() {
  let subs = new Subscription();
  return {
    sink(sub) {
      if (!subs) {
        miniRxError('Store has already been destroyed.');
      }
      subs?.add(sub);
    },
    unsubscribe: () => {
      subs?.unsubscribe();
      subs = undefined;
    }
  };
}

function createMiniRxActionType(operationType, featureKey, name) {
  return `${miniRxNameSpace}${featureKey ? '/' + featureKey : ''}/${operationType}${name ? '/' + name : ''}`;
}

function combineMetaReducers(metaReducers) {
  return reducer => {
    return metaReducers.reduceRight((previousValue, currentValue) => {
      return currentValue(previousValue);
    }, reducer);
  };
}

function sortExtensions(extensions) {
  // TODO can be `toSorted` (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/toSorted) when available in TS
  return [...extensions].sort((a, b) => {
    return a.sortOrder - b.sortOrder;
  });
}

function mapResponse(mapFn, errorFn) {
  return source => source.pipe(map(v => mapFn(v)), mergeMap(mapFnResult => {
    return Array.isArray(mapFnResult) ? from(mapFnResult) : of(mapFnResult);
  }), catchError(err => {
    const errorFnResult = errorFn(err);
    return errorFnResult ? Array.isArray(errorFnResult) ? from(errorFnResult) : of(errorFnResult) : EMPTY;
  }));
}

function miniRxConsoleError(message, err) {
  console.error(miniRxNameSpace + ': ' + message + '\nDetails:', err);
}

function isFunction(v) {
  return typeof v === 'function';
}

// Credits go to NgRx
function tapResponse(fn1OrObject, fn2, fn3) {
  return source => {
    const tapResponseObj = isFunction(fn1OrObject) ? {
      next: fn1OrObject,
      error: fn2,
      finalize: fn3
    } : fn1OrObject;
    return source.pipe(tap({
      next: v => {
        if (tapResponseObj.next) {
          tryCatch(() => tapResponseObj.next(v), 'next');
        }
      },
      error: err => tryCatch(() => tapResponseObj.error(err), 'error')
    }), catchError(() => EMPTY), tapResponseObj.finalize ? finalize(() => {
      tryCatch(() => tapResponseObj.finalize(), 'finalize');
    }) : identity // Conditionally apply operator: https://rxjs.dev/api/index/function/identity
    );
  };
}
function tryCatch(fn, whichCallback) {
  try {
    fn();
  } catch (error) {
    miniRxConsoleError(`An error occurred in the \`tapResponse\` ${whichCallback} callback.`, error);
  }
}

function ofType(...allowedTypes) {
  return filter(action => allowedTypes.some(type => {
    return type === action.type;
  }));
}

function createActionsOnQueue() {
  const actionsSource = new Subject();
  return {
    actions$: actionsSource.asObservable().pipe(observeOn(queueScheduler) // Prevent stack overflow: https://blog.cloudboost.io/so-how-does-rx-js-queuescheduler-actually-work-188c1b46526e
    ),
    dispatch: action => {
      actionsSource.next(action);
      return action;
    }
  };
}

// Credits go to NgRx
// Prevent effect to unsubscribe from the actions stream
function defaultEffectsErrorHandler(observable$, retryAttemptsLeft = 10) {
  return observable$.pipe(catchError(error => {
    miniRxConsoleError(`An error occurred in the Effect. MiniRx resubscribed the Effect automatically and will do so ${retryAttemptsLeft - 1} more times.\nPlease provide error handling inside the Effect using \`catchError\` or \`tapResponse\`.`, error);
    if (retryAttemptsLeft <= 1) {
      return observable$; // last attempt
    }
    // Return observable that produces this particular effect
    return defaultEffectsErrorHandler(observable$, retryAttemptsLeft - 1);
  }));
}

// Credits go to NgRx
// Copied from with small modifications: https://github.com/ngrx/platform/blob/14.3.0/modules/effects/src/effect_creator.ts
const EFFECT_METADATA_KEY = '@mini-rx/effectMetaData';
const DEFAULT_EFFECT_CONFIG = {
  dispatch: true
};
function createRxEffect(v, config) {
  const metaData = {
    ...DEFAULT_EFFECT_CONFIG,
    ...config
  };
  Object.defineProperty(v, EFFECT_METADATA_KEY, {
    value: metaData
  });
  return v;
}
function hasEffectMetaData(param) {
  return Object.hasOwn(param, EFFECT_METADATA_KEY);
}

class StoreExtension {
  sortOrder = 0 /* ExtensionSortOrder.DEFAULT */;
}
class Actions extends Observable {}

const stateOrCallbackKey = 'stateOrCallback';
// Type predicate
function isMiniRxAction(action) {
  return Object.hasOwn(action, stateOrCallbackKey);
}

// Only display type and payload in the LoggingExtension and Redux DevTools
function beautifyAction(action) {
  return isMiniRxAction(action) ? {
    type: action.type,
    payload: action.stateOrCallback
  } : action;
}

function loggerMetaReducer(reducer) {
  return (state, action) => {
    const actionToLog = beautifyAction(action);
    const nextState = reducer(state, action);
    console.log('%c' + action.type, 'color: #25c2a0', '\nAction:', actionToLog, '\nState: ', nextState);
    return nextState;
  };
}

class LoggerExtension extends StoreExtension {
  id = 2 /* ExtensionId.LOGGER */;
  hasCsSupport = true;
  init() {
    return loggerMetaReducer;
  }
}

const UNDO_ACTION = miniRxNameSpace + '/undo';
function undo(action) {
  return {
    type: UNDO_ACTION,
    payload: action
  };
}

function createUndoMetaReducer(bufferSize) {
  let executedActions = [];
  let initialState;
  return rootReducer => {
    return (state, action) => {
      if (action.type === UNDO_ACTION) {
        // if the action is UNDO_ACTION,
        // then call all the actions again on the rootReducer,
        // except the one we want to rollback
        let newState = initialState;
        executedActions = executedActions.filter(eAct => eAct !== action.payload);
        // update the state for every action until we get the
        // exact same state as before, but without the action we want to rollback
        executedActions.forEach(executedAction => {
          newState = rootReducer(newState, executedAction);
        });
        return newState;
      } else {
        // push every action that isn't UNDO_ACTION
        executedActions.push(action);
      }
      const updatedState = rootReducer(state, action);
      if (executedActions.length === bufferSize + 1) {
        const firstAction = executedActions[0];
        // calculate the state x (bufferSize) actions ago
        initialState = rootReducer(initialState, firstAction);
        // keep the correct actions
        executedActions = executedActions.slice(1, bufferSize + 1);
      }
      return updatedState;
    };
  };
}

// Credits go to Brecht Billiet
const defaultBufferSize = 100;
class UndoExtension extends StoreExtension {
  config;
  id = 1 /* ExtensionId.UNDO */;
  sortOrder = 1 /* ExtensionSortOrder.UNDO_EXTENSION */;
  hasCsSupport = true;
  constructor(config = {
    bufferSize: defaultBufferSize
  }) {
    super();
    this.config = config;
  }
  init() {
    return createUndoMetaReducer(this.config.bufferSize);
  }
}

// Copied from with small modifications: https://github.com/jsdf/deep-freeze/blob/v1.1.1/index.js
function deepFreeze(o) {
  Object.freeze(o);
  const oIsFunction = isFunction(o);
  const hasOwnProp = Object.prototype.hasOwnProperty;
  Object.getOwnPropertyNames(o).forEach(function (prop) {
    if (hasOwnProp.call(o, prop) && (oIsFunction ? prop !== 'caller' && prop !== 'callee' && prop !== 'arguments' : true) && o[prop] !== null && (typeof o[prop] === 'object' || isFunction(o[prop])) && !Object.isFrozen(o[prop])) {
      deepFreeze(o[prop]);
    }
  });
  return o;
}

function immutableStateMetaReducer(reducer) {
  return (state, action) => {
    if (state) {
      deepFreeze(state);
    }
    const nextState = reducer(state, action);
    deepFreeze(nextState);
    return nextState;
  };
}

// Credits go to Brandon Roberts
class ImmutableStateExtension extends StoreExtension {
  id = 0 /* ExtensionId.IMMUTABLE_STATE */;
  hasCsSupport = true;
  init() {
    return immutableStateMetaReducer;
  }
}

const defaultOptions = {
  name: 'MiniRx - Redux DevTools',
  traceLimit: 25
};
class AbstractReduxDevtoolsExtension extends StoreExtension {
  options;
  id = 3 /* ExtensionId.REDUX_DEVTOOLS */;
  devtoolsExtension;
  devtoolsConnection;
  _optionsForNgExtension;
  // sounds to much like Angular ;)
  // Used only in mini-rx-store-ng
  // TODO create a normal getter for options: get options()
  get optionsForNgExtension() {
    return this._optionsForNgExtension;
  }
  constructor(options) {
    super();
    this.options = options;
    if (!window) {
      miniRxError('The Redux DevTools are only supported in browser environments.');
    }
    this._optionsForNgExtension = options;
    this.devtoolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__;
    this.options = {
      ...defaultOptions,
      ...this.options
    };
  }
  init() {
    if (this.devtoolsExtension) {
      this.devtoolsConnection = this.devtoolsExtension.connect(this.options);
      this.actions$.pipe(tap(action => {
        const appState = this.readState();
        const actionForDevTools = beautifyAction(action);
        this.devtoolsConnection.send(actionForDevTools, appState);
      })).subscribe();
      this.devtoolsConnection.subscribe(this.onDevToolsMessage.bind(this));
    }
  }
  onDevToolsMessage(message) {
    if (message.type === "DISPATCH" /* DevToolActions.DISPATCH */) {
      switch (message.payload.type) {
        case "JUMP_TO_STATE" /* DevToolActions.JUMP_TO_STATE */:
        case "JUMP_TO_ACTION" /* DevToolActions.JUMP_TO_ACTION */:
          this.updateState(JSON.parse(message.state));
      }
    }
  }
}

function calcNextState(state, stateOrCallback) {
  return {
    ...state,
    ...(isFunction(stateOrCallback) ? stateOrCallback(state) : stateOrCallback) // new partial state
  };
}

function createFeatureStoreReducer(featureId, initialState) {
  return (state = initialState, action) => {
    return isMiniRxAction(action) && action.featureId === featureId ? calcNextState(state, action.stateOrCallback) : state;
  };
}

function createComponentStoreReducer(initialState) {
  return (state = initialState, action) => {
    return isMiniRxAction(action) ? calcNextState(state, action.stateOrCallback) : state;
  };
}

// Simple alphanumeric ID: https://stackoverflow.com/a/12502559/453959
// This isn't a real GUID!
function generateId() {
  return Math.random().toString(36).slice(2);
}

function mergeComponentStoreExtensions(globalExtensions, localExtensions) {
  // Local extensions overwrite the global extensions
  // If extension is global and local => use local
  // If extension is only global => use global
  // If extension is only local => use local
  // In other words: Always use local, but also add global-only extensions
  const globalOnlyExtensions = globalExtensions.reduce((accumulated, globalExt) => {
    const hasFoundGlobalOnlyExtension = !localExtensions.find(localExt => localExt.id === globalExt.id);
    return hasFoundGlobalOnlyExtension ? [...accumulated, globalExt] : accumulated;
  }, []);
  return [...localExtensions, ...globalOnlyExtensions];
}
function calculateExtensions(localConfig, globalConfig) {
  const extensions = mergeComponentStoreExtensions(globalConfig?.extensions ?? [], localConfig?.extensions ?? []);
  return sortExtensions(extensions);
}

// Credits go to NgRx
// Copied from with small modifications: https://github.com/ngrx/platform/blob/12.0.0/modules/store/src/utils.ts
function combineReducers(reducers) {
  const reducerKeys = Object.keys(reducers);
  const reducerKeyLength = reducerKeys.length;
  return (state = {}, action) => {
    const stateKeysLength = Object.keys(state).length;
    let hasChanged = stateKeysLength !== reducerKeyLength;
    const nextState = {};
    for (let i = 0; i < reducerKeyLength; i++) {
      const key = reducerKeys[i];
      const reducer = reducers[key];
      const previousStateForKey = state[key];
      const nextStateForKey = reducer(previousStateForKey, action);
      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }
    return hasChanged ? nextState : state;
  };
}

function createReducerWithInitialState(reducer, initialState) {
  return (state = initialState, action) => {
    return reducer(state, action);
  };
}
function omit(object, keyToOmit) {
  return Object.keys(object).filter(key => key !== keyToOmit).reduce((prevValue, key) => {
    prevValue[key] = object[key];
    return prevValue;
  }, {});
}
function createReducerManager() {
  let state = {
    featureReducers: {},
    metaReducers: []
  };
  let reducer = state => state; // Simple default reducer (needed to initialize the store also if there are no reducers registered yet)
  function _updateStateAndReducer(v) {
    state = {
      ...state,
      ...v
    };
    const combinedMetaReducer = combineMetaReducers(state.metaReducers);
    const combinedReducer = combineReducers(state.featureReducers);
    reducer = combinedMetaReducer(combinedReducer);
  }
  function setFeatureReducers(featureReducers) {
    _updateStateAndReducer({
      featureReducers
    });
  }
  function addFeatureReducer(featureKey, reducer, metaReducers, initialState) {
    if (Object.hasOwn(state.featureReducers, featureKey)) {
      miniRxError(`Feature "${featureKey}" already exists.`);
    }
    reducer = metaReducers?.length ? combineMetaReducers(metaReducers)(reducer) : reducer;
    if (initialState) {
      reducer = createReducerWithInitialState(reducer, initialState);
    }
    _updateStateAndReducer({
      featureReducers: {
        ...state.featureReducers,
        [featureKey]: reducer
      }
    });
  }
  function removeFeatureReducer(featureKey) {
    _updateStateAndReducer({
      featureReducers: omit(state.featureReducers, featureKey)
    });
  }
  function addMetaReducers(...reducers) {
    _updateStateAndReducer({
      metaReducers: [...state.metaReducers, ...reducers]
    });
  }
  return {
    setFeatureReducers,
    addFeatureReducer,
    removeFeatureReducer,
    addMetaReducers,
    get reducer() {
      return reducer;
    }
  };
}

function componentStoreConfig() {
  let componentStoreConfig = undefined;
  function set(config) {
    if (!componentStoreConfig) {
      componentStoreConfig = config;
      return;
    }
    miniRxError('ComponentStore config was set multiple times.');
  }
  function get() {
    return componentStoreConfig;
  }
  return {
    set,
    get
  };
}

export { AbstractReduxDevtoolsExtension, Actions, EFFECT_METADATA_KEY, ImmutableStateExtension, LoggerExtension, StoreExtension, UndoExtension, calculateExtensions, combineMetaReducers, componentStoreConfig, createActionsOnQueue, createComponentStoreReducer, createFeatureStoreReducer, createMiniRxActionType, createReducerManager, createRxEffect, createSubSink, defaultEffectsErrorHandler, generateId, hasEffectMetaData, mapResponse, miniRxError, miniRxNameSpace, ofType, sortExtensions, tapResponse, undo };
